import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card, Table, Tag, Drawer, Descriptions, Select, Input, Button, message, Typography,
} from 'antd';
import { apiClient } from 'Utils/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Contact {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  message: string;
  newsletterOptIn: boolean;
  status: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
  internalNotes: string | null;
  createdAt: string;
}

const COLORS: Record<string, string> = { OPEN: 'red', IN_PROGRESS: 'orange', CLOSED: 'default' };

const ContactsPage: React.FC = () => {
  const [items, setItems] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState<Contact | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/contacts');
      setItems(res.data?.contacts || []);
    } catch (e: any) { message.error(e.response?.data?.message || 'Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const open = (c: Contact) => { setActive(c); setNotes(c.internalNotes || ''); };

  // Open the request linked from the notification email (/…/:id)
  const { id } = useParams();
  const deepLinkOpened = useRef(false);
  useEffect(() => {
    const r = items.find((i) => i.id === id);
    if (r && !deepLinkOpened.current) { deepLinkOpened.current = true; open(r); }
  }, [items, id]);

  const save = async (patch: any) => {
    if (!active) return;
    setSaving(true);
    try {
      const r = await apiClient.patch(`/api/admin/contacts/${active.id}`, patch);
      if (r.data?.status === 'success') { message.success('Aktualisiert'); setActive(r.data.contact); load(); }
    } catch (e: any) { message.error(e.response?.data?.message || 'Fehler'); }
    finally { setSaving(false); }
  };

  const columns = [
    { title: 'Eingegangen', dataIndex: 'createdAt', render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm') },
    { title: 'Name', dataIndex: 'name', render: (v: string | null) => v || <Text type="secondary">—</Text> },
    { title: 'E-Mail', dataIndex: 'email' },
    { title: 'Telefon', dataIndex: 'phone', render: (v: string | null) => v || <Text type="secondary">—</Text> },
    { title: 'Newsletter', dataIndex: 'newsletterOptIn', render: (v: boolean) => v ? <Tag color="green">Ja</Tag> : <Tag>Nein</Tag> },
    { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag color={COLORS[s]}>{s}</Tag> },
    { title: '', key: 'a', render: (_: any, r: Contact) => <Button size="small" onClick={() => open(r)}>Öffnen</Button> },
  ];

  return (
    <Card>
      <Title level={3}>Kontaktanfragen</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Übersicht aller über das Kontaktformular eingegangenen Anfragen.
      </Text>
      <Table rowKey="id" loading={loading} dataSource={items} columns={columns as any}
        pagination={{ pageSize: 20 }} data-testid="contacts-table" />

      <Drawer open={!!active} title={active ? `Anfrage · ${active.email}` : ''} width={600}
        onClose={() => setActive(null)}>
        {active && (
          <>
            <Select
              value={active.status}
              style={{ width: 200, marginBottom: 16 }}
              onChange={(v) => save({ status: v })}
              options={[
                { value: 'OPEN', label: 'Offen' },
                { value: 'IN_PROGRESS', label: 'In Bearbeitung' },
                { value: 'CLOSED', label: 'Geschlossen' },
              ]}
            />
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Eingegangen">{dayjs(active.createdAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Name">{active.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="E-Mail">{active.email}</Descriptions.Item>
              <Descriptions.Item label="Telefon">{active.phone || '—'}</Descriptions.Item>
              <Descriptions.Item label="Newsletter">{active.newsletterOptIn ? 'Ja' : 'Nein'}</Descriptions.Item>
              <Descriptions.Item label="Nachricht">
                <div style={{ whiteSpace: 'pre-wrap' }}>{active.message}</div>
              </Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 16 }}>
              <Text strong>Interne Notizen</Text>
              <TextArea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ marginTop: 6 }} />
              <Button type="primary" style={{ marginTop: 8 }} loading={saving}
                onClick={() => save({ internalNotes: notes })}>Notizen speichern</Button>
            </div>
          </>
        )}
      </Drawer>
    </Card>
  );
};

export default ContactsPage;
